import React, { useState, useEffect, useCallback } from "react";
import { Mail, Edit as EditIcon, Trash2, Eye } from 'lucide-react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Chip,
    FormControlLabel,
    Checkbox
} from '@mui/material';

import { userSelector } from "../../../user-redux/selector";
import { useSelector } from "react-redux";
import axios from "axios";
import Key from "../../../../clientVariables.json";
import SignatureEditor from "../../../SignatureEditor";

const SignatureSection = () => {
    const { user } = useSelector(userSelector);
    const [openSignatureDialog, setOpenSignatureDialog] = useState(false);
    const [signatureName, setSignatureName] = useState("");
    const [signatureHtml, setSignatureHtml] = useState("");
    const [isSavingSignature, setIsSavingSignature] = useState(false);
    const [savedSignatures, setSavedSignatures] = useState([]);
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [openToast, setOpenToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [sigToDelete, setSigToDelete] = useState(null);
    const [isDefault, setIsDefault] = useState(false);
    const [isSettingDefault, setIsSettingDefault] = useState(false);

    const fetchSignatures = useCallback(async () => {
        try {
            if (user?.id) {
                const response = await axios.post(`${Key.domain}/user/get-employee-signature`, {
                    user_id: user.id
                });
                if (response.data.success === 1) {
                    const signatures = Array.isArray(response.data.data) ? response.data.data : (response.data.data?.data || []);
                    setSavedSignatures(signatures);
                }
            }
        } catch (error) {
            console.error("Error fetching signatures:", error);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchSignatures();
    }, [fetchSignatures]);

    const handleSaveSignature = async () => {
        if (!signatureName.trim() || !signatureHtml.trim() || signatureHtml === "<p><br></p>") {
            setToastMessage("Please provide both name and signature content");
            setOpenToast(true);
            return;
        }

        setIsSavingSignature(true);
        try {
            const url = editingId
                ? `${Key.domain}/user/emp-update-signature`
                : `${Key.domain}/user/emp-save-signature`;

            const payload = {
                user_id: user?.id,
                signature_html: signatureHtml,
                signature_name: signatureName,
                is_default: isDefault ? 1 : 0
            };

            if (editingId) {
                payload.signature_id = editingId;
            }

            const response = await axios.post(url, payload);

            if (response.data.success === 1 || response.data.success === true) {
                setToastMessage(editingId ? "Signature updated successfully" : "Signature saved successfully");
                setOpenToast(true);
                handleCloseDialog();
                fetchSignatures();
            } else {
                setToastMessage(editingId ? "Failed to update signature" : "Failed to save signature");
                setOpenToast(true);
            }
        } catch (error) {
            console.error("Error saving/updating signature:", error);
            setToastMessage(error.response?.data?.error?.message || "Something went wrong");
            setOpenToast(true);
        } finally {
            setIsSavingSignature(false);
        }
    };

    const handleEditClick = (sig) => {
        setEditingId(sig.id);
        setSignatureName(sig.signature_name);
        setSignatureHtml(sig.signature_html);
        setIsDefault(sig.is_default === 1);
        setOpenSignatureDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenSignatureDialog(false);
        setEditingId(null);
        setSignatureName("");
        setSignatureHtml("");
        setIsDefault(false);
    };

    const handleDeleteSignature = (id) => {
        setSigToDelete(id);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!sigToDelete) return;

        try {
            const response = await axios.post(`${Key.domain}/user/emp-delete-signature`, {
                user_id: user.id,
                signature_id: sigToDelete
            });
            if (response.data.success === 1) {
                setToastMessage("Signature deleted successfully");
                setOpenToast(true);
                fetchSignatures();
            }
        } catch (error) {
            console.error("Error deleting signature:", error);
            setToastMessage("Failed to delete signature");
            setOpenToast(true);
        } finally {
            setOpenDeleteDialog(false);
            setSigToDelete(null);
        }
    };

    const handleSetDefault = async (id) => {
        setIsSettingDefault(true);
        try {
            const response = await axios.post(`${Key.domain}/user/emp-set-default-signature`, {
                user_id: user.id,
                signature_id: id
            });
            if (response.data.success === 1) {
                setToastMessage("Default signature updated");
                setOpenToast(true);
                fetchSignatures();
            }
        } catch (error) {
            console.error("Error setting default signature:", error);
            setToastMessage("Failed to set default signature");
            setOpenToast(true);
        } finally {
            setIsSettingDefault(false);
        }
    };

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: 1,
                border: '1px solid',
                borderColor: 'grey.100',
                backgroundColor: 'white',
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Mail size={24} style={{ marginRight: 8, color: "#3b82f6" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                    Email Signatures
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleCloseDialog() || setOpenSignatureDialog(true)}
                    sx={{ textTransform: 'none', borderRadius: '20px' }}
                >
                    Add New
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '400px' }}>
                {savedSignatures.length > 0 ? (
                    <List disablePadding>
                        {savedSignatures.map((sig) => (
                            <ListItem
                                key={sig.id}
                                divider
                                sx={{
                                    px: 0,
                                    '&:last-child': { borderBottom: 'none' }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {sig.signature_name}
                                            {sig.is_default === 1 && (
                                                <Chip 
                                                    label="Default" 
                                                    size="small" 
                                                    color="primary" 
                                                    sx={{ 
                                                        ml: 1, 
                                                        height: '18px', 
                                                        fontSize: '10px',
                                                        backgroundImage: 'linear-gradient(to right, #42a5f5, #1e88e5)',
                                                    }} 
                                                />
                                            )}
                                        </Box>
                                    }
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                />
                                <ListItemSecondaryAction>
                                    <Tooltip title="Preview">
                                        <IconButton size="small" onClick={() => { setPreviewHtml(sig.signature_html); setOpenPreviewDialog(true); }}>
                                            <Eye size={18} />
                                        </IconButton>
                                    </Tooltip>
                                    {sig.is_default !== 1 && (
                                        <Tooltip title="Set as Default">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleSetDefault(sig.id)}
                                                disabled={isSettingDefault}
                                            >
                                                <Mail size={18} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Edit">
                                        <IconButton size="small" sx={{ color: 'primary.main' }} onClick={() => handleEditClick(sig)}>
                                            <EditIcon size={18} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => handleDeleteSignature(sig.id)}>
                                            <Trash2 size={18} />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                        No signatures saved yet.
                    </Typography>
                )}
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openSignatureDialog} onClose={() => !isSavingSignature && handleCloseDialog()} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>{editingId ? "Edit Email Signature" : "Create Email Signature"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Signature Name</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="e.g., Official Signature"
                            value={signatureName}
                            onChange={(e) => setSignatureName(e.target.value)}
                        />
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Signature Content</Typography>
                        <SignatureEditor
                            value={signatureHtml}
                            onChange={setSignatureHtml}
                            height={300}
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={isDefault} 
                                    onChange={(e) => setIsDefault(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={<Typography variant="body2">Set as default signature</Typography>}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog} disabled={isSavingSignature} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveSignature}
                        disabled={isSavingSignature}
                        sx={{
                            textTransform: "none",
                            backgroundImage: 'linear-gradient(to right, #42a5f5, #1e88e5)',
                        }}
                    >
                        {isSavingSignature ? <CircularProgress size={24} color="inherit" /> : (editingId ? "Update Signature" : "Save Signature")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Signature Preview</DialogTitle>
                <DialogContent dividers>
                    <Box dangerouslySetInnerHTML={{ __html: previewHtml }} sx={{ p: 3, border: '1px solid #eee', borderRadius: '8px' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPreviewDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete this signature? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            bgcolor: '#ef4444',
                            fontWeight: 700,
                            '&:hover': { bgcolor: '#dc2626' }
                        }}
                    >
                        Delete Permanent
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={openToast} autoHideDuration={4000} onClose={() => setOpenToast(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setOpenToast(false)} severity={toastMessage.includes("Failed") ? "error" : "success"} sx={{ width: '100%' }}>
                    {toastMessage}
                </Alert>
            </Snackbar>
        </Card>
    );
};

export default SignatureSection;
